/* eslint-disable react/no-array-index-key */
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import format from "date-fns/format";

// Material
import { makeStyles } from "@material-ui/core/styles";
import CardContent from "@material-ui/core/CardContent";
import Tooltip from "@material-ui/core/Tooltip";

// Config
import { submissionStatus } from "../../config/values";
import styles from "../../config/styles";
import useTranslate from "../../hooks/useTranslate";
import useIncidentTypes from "src/hooks/useIncidentTypes";

// Components
import Avatar from "../Avatar";
import StatusChip from "../StatusChip";
import Text from "../Text";
import colors from "src/config/colors";
import PopOver from "../PopOver";
import { ButtonBase } from "@material-ui/core";
import MiniMap from "../MiniMap";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3, 5, 0)
  },
  summaryWrapper: {
    borderBottom: styles.values.border,
    paddingBottom: theme.spacing(1.5)
  },
  summaryTitle: {
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.25)
  },
  summaryRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    margin: theme.spacing(1.25, 0),
    gap: theme.spacing(1),

    "&:last-child": {
      margin: 0
    }
  },
  valueLabel: {
    whiteSpace: "nowrap",
    minWidth: "120px"
  },
  value: {
    marginLeft: "auto",
    textAlign: "right",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  valueClickable: {
    marginLeft: "auto",
    textAlign: "right",
    color: colors.pictonBlue,
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  mapWrapper: {
    width: "300px"
  },
  chipWrapper: {
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
    gap: 3
  },
  chip: {
    borderRadius: theme.shape.borderRadius,
    marginLeft: "auto",
    height: 20
  },
  avatar: {
    height: 26,
    width: 26,
    fontSize: 12
  }
}));

const CardSummary = ({
  mediaItems,
  status,
  users,
  incidentDate,
  incidentTypeId,
  location,
  isIntel
}) => {
  const classes = useStyles();
  const getText = useTranslate();
  const { isLoading: isLoadingIncidentTypes, data: incidentTypes } =
    useIncidentTypes();
  const incompleteText = getText("common.prompts.incomplete");

  const incidentType = useMemo(() => {
    if (!incidentTypeId) return incompleteText;
    if (isLoadingIncidentTypes || !incidentTypes) return null;
    return incidentTypes.find(item => item.id === incidentTypeId)?.name;
  }, [incidentTypeId, isLoadingIncidentTypes, incidentTypes]);

  return (
    <CardContent className={classes.root}>
      <div className={classes.summaryWrapper}>
        <Text
          className={classes.summaryTitle}
          text="caseDetailPage.caseInfoCard.summaryTitle"
          type="contentLabel"
        />
        <div className={classes.summaryRow}>
          <Text
            text="common.valueLabels.submissionStatus"
            type="valueLabel"
            className={classes.valueLabel}
          />
          {status === submissionStatus.NONE ? (
            <span className={classes.value}>
              {getText("common.prompts.none")}
            </span>
          ) : (
            <div className={classes.chipWrapper}>
              <StatusChip status={status} />
            </div>
          )}
        </div>
        <div className={classes.summaryRow}>
          <Text
            text="caseIncidentsPage.incidentTypeLabel"
            type="valueLabel"
            className={classes.valueLabel}
          />
          <span className={classes.value}>{incidentType}</span>
        </div>
        <div className={classes.summaryRow}>
          <Text
            text="common.valueLabels.incidentDate"
            type="valueLabel"
            className={classes.valueLabel}
          />
          <span className={classes.value}>
            {incidentDate
              ? format(
                  new Date(incidentDate),
                  getText("common.dateFormat.shortDateAndTime")
                )
              : incompleteText}
          </span>
        </div>
        <div className={classes.summaryRow}>
          <Text
            text="common.valueLabels.location"
            type="valueLabel"
            className={classes.valueLabel}
          />
          {location ? (
            <PopOver
              render={({ onClick }) => (
                <Text onClick={onClick} className={classes.valueClickable}>
                  {location.name}
                </Text>
              )}
            >
              <div className={classes.mapWrapper}>
                <MiniMap
                  latitude={location.coordinate.latitude}
                  longitude={location.coordinate.longitude}
                />
              </div>
            </PopOver>
          ) : (
            <span className={classes.value}>{incompleteText}</span>
          )}
        </div>
        <div className={classes.summaryRow}>
          <Text
            text="common.valueLabels.evidenceCaptured"
            type="valueLabel"
            className={classes.valueLabel}
          />
          <span className={classes.value}>{mediaItems}</span>
        </div>
        <div className={classes.summaryRow}>
          {users &&
            users.map(({ fullName, profilePictureURL, colorHex }, index) => (
              <Tooltip title={fullName} key={index}>
                <Avatar
                  className={classes.avatar}
                  src={profilePictureURL}
                  backgroundColor={
                    colorHex || (isIntel ? colors.royalBlue : undefined)
                  }
                >
                  {fullName.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
        </div>
      </div>
    </CardContent>
  );
};

CardSummary.propTypes = {
  status: PropTypes.string.isRequired,
  mediaItems: PropTypes.number.isRequired,
  incidentTypeId: PropTypes.string,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string,
      id: PropTypes.string,
      profilePictureURL: PropTypes.string,
      colorHex: PropTypes.string
    })
  ),
  incidentDate: PropTypes.string,
  location: PropTypes.shape({
    name: PropTypes.string,
    coordinate: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number
    })
  }),
  isIntel: PropTypes.bool
};

CardSummary.defaultProps = {
  incidentTypeId: null,
  incidentDate: "",
  users: null,
  location: null,
  isIntel: false
};

export default CardSummary;
